import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Projects from './pages/projects/Projects.jsx';
import { motion } from 'framer-motion';
import Home from './pages/home/Home.jsx';
import News from './pages/news/News.jsx';
import NavBar from './components/NavBar.jsx';
import Updates from './pages/updates/Updates.jsx';
import { AchievementsPanel, Footer } from './components/minor.jsx';
import './App.css';
import ArticlePage from './pages/news/article/ArticlePage.jsx';
import Authors from './pages/authors/Authors.jsx';
import AuthorsList from './pages/authors/AuthorsList.jsx';
import Roadmap from './pages/roadmap/Roadmap.jsx';
import { dataService } from './services/dataService.js';
import CookieConsent from './components/CookieConsent.jsx';

// Komponent wewnętrzny do obsługi routingu
const AppContent = ({ loading, projects, news, authors, announcements, achievements, updates, glossary, WWD, roadmap }) => {
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [currentSection, setCurrentSection] = useState('home');
  const [isAchievementsPanelOpen, setIsAchievementsPanelOpen] = useState(false);

  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [perfMode, setPerfMode] = useState(() => localStorage.getItem('perfMode') === 'true');

  useEffect(() => {
    document.documentElement.classList.toggle('theme-light', theme === 'light');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => { localStorage.setItem('lang', lang); }, [lang]);
  useEffect(() => { localStorage.setItem('perfMode', perfMode); }, [perfMode]);

  const openAchievementsPanel = () => {
    setIsAchievementsPanelOpen(true);
    document.body.classList.add('achievements-open');
  };

  const closeAchievementsPanel = () => {
    setIsAchievementsPanelOpen(false);
    document.body.classList.remove('achievements-open');
  };

  // eslint-disable-next-line no-unused-vars
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 }
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.5
  };

  // Sprawdź czy aktualny path to profiles lub profiles/:id
  const isProfilesPage = location.pathname === '/profiles' || location.pathname.startsWith('/profiles/') || location.pathname.startsWith('/projects') || location.pathname.startsWith('/updates');

  return (
    <div className="app-container">
      <NavBar
        loading={loading}
        projects={projects}
        news={news}
        updates={updates}
        authors={authors}
        announcements={announcements}
        theme={theme}
        onThemeToggle={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
        lang={lang}
        onLangToggle={() => setLang(l => l === 'en' ? 'pl' : 'en')}
        perfMode={perfMode}
        onPerfModeToggle={() => setPerfMode(p => !p)}
      />
      <AchievementsPanel isOpen={isAchievementsPanelOpen} onClose={closeAchievementsPanel} achievements={achievements} />
      <motion.main
        className="main-content"
        initial="initial"
        animate="in"
        exit="out"
        transition={pageTransition}
      >
        <Routes>
          <Route
            path="/"
            element={
              <Home
                loading={loading}
                projects={projects}
                news={news}
                authors={authors}
                WWD={WWD}
                announcements={announcements}
                achievements={achievements}
                roadmap={roadmap}
                onOpenAchievements={openAchievementsPanel}
                perfMode={perfMode}
              />
            }
          />
          <Route
            path="/projects"
            element={
              <Projects
                loading={loading}
                projects={projects}
                news={news}
                announcements={announcements}
                authors={authors}
              />
            }
          />
          <Route
            path="/news"
            element={
              <News
                loading={loading}
                projects={projects}
                news={news}
                authors={authors}
                announcements={announcements}
              />
            }
          />
          <Route
            path="/updates"
            element={
              <Updates
                loading={loading}
                projects={projects}
                updates={updates}
              />
            }
          />
          <Route
            path="/profiles"
            element={
              <AuthorsList
                loading={loading}
                authors={authors}
                news={news}
                announcements={announcements}
              />
            }
          />
          <Route
            path="/article/*"
            element={
              <ArticlePage
                loading={loading}
                projects={projects}
                news={news}
                announcements={announcements}
                authors={authors}
                glossary={glossary}
              />
            }
          />
          <Route
            path="/profiles/:id"
            element={
              <Authors
                loading={loading}
                authors={authors}
                news={news}
                announcements={announcements}
                achievements={achievements}
              />
            }
          />
          <Route path="/roadmap" element={<Roadmap roadmap={roadmap} />} />
        </Routes>
      </motion.main>
      <Footer animate={!isProfilesPage} />
    </div>
  );
};

const Loader = ({ exiting }) => (
  <div className={`tq-loader${exiting ? ' tq-loader--exiting' : ''}`}>
    <div className="tq-word" aria-label="TEHAQUE">
      <span className="tq-l tq-l--t">T</span>
      <span className="tq-l tq-l--e1">E</span>
      <span className="tq-l tq-l--h">H</span>
      <span className="tq-l tq-l--a">A</span>
      <span className="tq-l tq-l--q">Q</span>
      <span className="tq-l tq-l--u">U</span>
      <span className="tq-l tq-l--e2">E</span>
    </div>
    <div className="tq-loader-line" />
    <div className="tq-loader-sub">tehaque.dev</div>
  </div>
);

function App() {
  const [dataLoading, setDataLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [minTimePassed, setMinTimePassed] = useState(false);
  const [loaderExiting, setLoaderExiting] = useState(false);
  const [loaderGone, setLoaderGone] = useState(false);
  const [data, setData] = useState({
    authors: [],
    achievements: {},
    categories: [],
    announcements: [],
    WWD: [],
    projects: [],
    news: [],
    glossary: {},
    roadmap: [],
    updates: []
  });

  // Minimum display time so the letter animation completes (~2.4s)
  useEffect(() => {
    const t = setTimeout(() => setMinTimePassed(true), 2400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const fetched = await dataService.fetchData();
        if (!mounted) return;
        setData({
          authors: fetched.authors || [],
          achievements: fetched.achievements || {},
          categories: fetched.categories || [],
          announcements: fetched.announcements || [],
          WWD: fetched.WWD || [],
          projects: fetched.projects || [],
          news: fetched.news || [],
          glossary: fetched.glossary || {},
          roadmap: fetched.roadmap || [],
          updates: fetched.updates || []
        });
      } catch (e) {
        console.error('Błąd pobierania danych:', e);
      } finally {
        if (mounted) { setDataLoading(false); setDataReady(true); }
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Dismiss loader only when both conditions are met
  useEffect(() => {
    if (dataReady && minTimePassed && !loaderExiting && !loaderGone) {
      setLoaderExiting(true);
      const t = setTimeout(() => setLoaderGone(true), 750);
      return () => clearTimeout(t);
    }
  }, [dataReady, minTimePassed, loaderExiting, loaderGone]);

  return (
    <>
      {!loaderGone && <Loader exiting={loaderExiting} />}
      <Router>
        <AppContent loading={dataLoading} {...data} />
      </Router>
      <CookieConsent />
    </>
  );
}

export default App;
